/**
 * LLM-9F — athlete-facing saved coaching memory management (read + conversation-bound forget).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Brain, Loader2 } from "lucide-react";

import {
  createCoachingSession,
  fetchCoachingMemoryManagement,
  sendCoachingSessionMessage,
} from "@/api/aiCoachApi";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/layout/EmptyState";
import { SectionHeader } from "@/components/layout/SectionHeader";
import { SportBadge } from "@/components/SportBadge";
import {
  CONFIRM_REPLY,
  FORGET_ALL_UI_STATE,
  MEMORY_ITEM_UI_STATE,
  REJECT_REPLY,
  buildSessionMessageRequest,
} from "@/lib/memoryManagementFlow";

const groupItems = (items) => {
  const globalItems = items.filter((item) => item.scope === "GLOBAL");
  const sportGroups = new Map();
  for (const item of items) {
    if (item.scope !== "SPORT" || !item.sport) continue;
    const bucket = sportGroups.get(item.sport) || [];
    bucket.push(item);
    sportGroups.set(item.sport, bucket);
  }
  return {
    globalItems,
    sportGroups: [...sportGroups.entries()].sort(([a], [b]) => a.localeCompare(b)),
  };
};

const MemoryRow = ({
  item,
  uiState,
  confirmationSummary,
  errorMessage,
  disabled,
  onForget,
  onConfirm,
  onCancel,
  testId,
}) => (
  <div
    className="app-card-elevated p-4 min-w-0"
    data-testid={testId}
  >
    <div className="text-sm text-dark-text-primary font-medium">{item.label}</div>
    <div className="text-sm text-dark-text-secondary mt-1">{item.value}</div>
    <div className="mt-2">
      {item.scope === "GLOBAL" ? (
        <span className="card-label">Global</span>
      ) : (
        <SportBadge sport={item.sport} />
      )}
    </div>

    {uiState === MEMORY_ITEM_UI_STATE.EXECUTED && (
      <p className="mt-3 text-sm text-analytics-green" role="status">
        Removed from saved preferences
      </p>
    )}
    {uiState === MEMORY_ITEM_UI_STATE.UNCERTAIN && (
      <p className="mt-3 text-sm text-analytics-amber" role="status">
        We couldn&apos;t confirm whether that preference was removed. Refresh your saved preferences to check.
      </p>
    )}
    {errorMessage && (
      <p className="mt-3 text-sm text-analytics-amber" role="alert">
        {errorMessage}
      </p>
    )}

    {uiState === MEMORY_ITEM_UI_STATE.AWAITING_CONFIRMATION && (
      <div className="mt-4 space-y-3" data-testid={`${testId}-confirmation`}>
        <p className="text-sm text-dark-text-secondary whitespace-pre-wrap">
          {confirmationSummary}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" disabled={disabled} onClick={onConfirm}>
            Confirm
          </Button>
          <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    )}

    {[
      MEMORY_ITEM_UI_STATE.IDLE,
      MEMORY_ITEM_UI_STATE.ERROR,
      MEMORY_ITEM_UI_STATE.CANCELLED,
      MEMORY_ITEM_UI_STATE.UNCERTAIN,
    ].includes(uiState) && (
      <div className="mt-4">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={onForget}
          aria-label={`Forget ${item.label}`}
        >
          Forget this
        </Button>
      </div>
    )}
  </div>
);

export const CoachingMemorySettings = ({ testId = "coaching-memory-settings" }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState({ items: [], forgetAllCommand: null });
  const [itemStates, setItemStates] = useState({});
  const [itemErrors, setItemErrors] = useState({});
  const [itemSummaries, setItemSummaries] = useState({});
  const [forgetAllState, setForgetAllState] = useState(FORGET_ALL_UI_STATE.IDLE);
  const [forgetAllSummary, setForgetAllSummary] = useState(null);
  const [forgetAllError, setForgetAllError] = useState(null);
  const [showForgetAllDialog, setShowForgetAllDialog] = useState(false);

  const sessionRef = useRef({ conversationId: null, revision: null });
  const requestLockRef = useRef(false);
  const activeItemRef = useRef(null);

  const refreshList = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await fetchCoachingMemoryManagement();
      if (!response || response.malformed) {
        setLoadError("We couldn't load your saved coaching preferences.");
        setData({ items: [], forgetAllCommand: null });
      } else {
        setData({ items: response.items, forgetAllCommand: response.forgetAllCommand });
      }
    } catch (err) {
      setLoadError("We couldn't load your saved coaching preferences.");
      setData({ items: [], forgetAllCommand: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const grouped = useMemo(() => groupItems(data.items), [data.items]);

  const destructiveBusy = requestLockRef.current
    || [
      FORGET_ALL_UI_STATE.REQUESTING,
      FORGET_ALL_UI_STATE.CONFIRMING,
      FORGET_ALL_UI_STATE.AWAITING_CONFIRMATION,
    ].includes(forgetAllState)
    || Object.values(itemStates).some((state) =>
      [
        MEMORY_ITEM_UI_STATE.REQUESTING,
        MEMORY_ITEM_UI_STATE.CONFIRMING,
        MEMORY_ITEM_UI_STATE.AWAITING_CONFIRMATION,
      ].includes(state));

  const ensureSession = useCallback(async () => {
    if (sessionRef.current.conversationId && sessionRef.current.revision != null) {
      return sessionRef.current;
    }
    const session = await createCoachingSession();
    sessionRef.current = {
      conversationId: session.conversation_id,
      revision: session.revision,
    };
    return sessionRef.current;
  }, []);

  const submitSessionMessage = useCallback(async (message) => {
    const { conversationId, revision } = await ensureSession();
    const response = await sendCoachingSessionMessage(
      conversationId,
      buildSessionMessageRequest({ message, expectedRevision: revision }),
    );
    sessionRef.current.revision = response.revision;
    return response;
  }, [ensureSession]);

  const handleForgetItem = async (presentationId, forgetCommand) => {
    if (requestLockRef.current || destructiveBusy) return;
    requestLockRef.current = true;
    activeItemRef.current = presentationId;
    setItemErrors((prev) => ({ ...prev, [presentationId]: null }));
    setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.REQUESTING }));
    try {
      const response = await submitSessionMessage(forgetCommand);
      if (response.confirmation_required) {
        setItemSummaries((prev) => ({
          ...prev,
          [presentationId]: response.pending_action_summary || "Confirm forget?",
        }));
        setItemStates((prev) => ({
          ...prev,
          [presentationId]: MEMORY_ITEM_UI_STATE.AWAITING_CONFIRMATION,
        }));
      } else if (response.executed) {
        setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.EXECUTED }));
        await refreshList();
        setItemStates({});
      } else {
        setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.ERROR }));
        setItemErrors((prev) => ({
          ...prev,
          [presentationId]: "Could not start forget confirmation.",
        }));
      }
    } catch (err) {
      setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.UNCERTAIN }));
      setItemErrors((prev) => ({
        ...prev,
        [presentationId]: err?.message || "Network error while requesting forget.",
      }));
      await refreshList();
    } finally {
      requestLockRef.current = false;
      activeItemRef.current = null;
    }
  };

  const handleConfirmItem = async (presentationId) => {
    if (requestLockRef.current) return;
    requestLockRef.current = true;
    setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.CONFIRMING }));
    try {
      const response = await submitSessionMessage(CONFIRM_REPLY);
      if (response.executed) {
        setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.EXECUTED }));
        await refreshList();
        setItemStates({});
      } else {
        setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.UNCERTAIN }));
        await refreshList();
      }
    } catch (err) {
      setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.UNCERTAIN }));
      await refreshList();
    } finally {
      requestLockRef.current = false;
    }
  };

  const handleCancelItem = async (presentationId) => {
    if (requestLockRef.current) return;
    requestLockRef.current = true;
    try {
      await submitSessionMessage(REJECT_REPLY);
      setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.CANCELLED }));
    } catch (err) {
      setItemStates((prev) => ({ ...prev, [presentationId]: MEMORY_ITEM_UI_STATE.UNCERTAIN }));
      await refreshList();
    } finally {
      requestLockRef.current = false;
    }
  };

  const handleForgetAllContinue = async () => {
    if (!data.forgetAllCommand || requestLockRef.current) return;
    requestLockRef.current = true;
    setShowForgetAllDialog(false);
    setForgetAllError(null);
    setForgetAllState(FORGET_ALL_UI_STATE.REQUESTING);
    try {
      const response = await submitSessionMessage(data.forgetAllCommand);
      if (response.confirmation_required) {
        setForgetAllSummary(response.pending_action_summary || "Confirm forget all?");
        setForgetAllState(FORGET_ALL_UI_STATE.AWAITING_CONFIRMATION);
      } else if (response.executed) {
        setForgetAllState(FORGET_ALL_UI_STATE.EXECUTED);
        await refreshList();
      } else {
        setForgetAllState(FORGET_ALL_UI_STATE.ERROR);
        setForgetAllError("Could not start forget-all confirmation.");
      }
    } catch (err) {
      setForgetAllState(FORGET_ALL_UI_STATE.UNCERTAIN);
      setForgetAllError("We couldn't confirm whether preferences were removed. Refresh to check.");
      await refreshList();
    } finally {
      requestLockRef.current = false;
    }
  };

  const handleForgetAllConfirm = async () => {
    if (requestLockRef.current) return;
    requestLockRef.current = true;
    setForgetAllState(FORGET_ALL_UI_STATE.CONFIRMING);
    try {
      const response = await submitSessionMessage(CONFIRM_REPLY);
      if (response.executed) {
        setForgetAllState(FORGET_ALL_UI_STATE.EXECUTED);
        await refreshList();
      } else {
        setForgetAllState(FORGET_ALL_UI_STATE.UNCERTAIN);
        await refreshList();
      }
    } catch (err) {
      setForgetAllState(FORGET_ALL_UI_STATE.UNCERTAIN);
      await refreshList();
    } finally {
      requestLockRef.current = false;
    }
  };

  const handleForgetAllCancel = async () => {
    if (requestLockRef.current) return;
    requestLockRef.current = true;
    try {
      if (forgetAllState === FORGET_ALL_UI_STATE.AWAITING_CONFIRMATION) {
        await submitSessionMessage(REJECT_REPLY);
      }
      setForgetAllState(FORGET_ALL_UI_STATE.CANCELLED);
    } catch (err) {
      setForgetAllState(FORGET_ALL_UI_STATE.UNCERTAIN);
      await refreshList();
    } finally {
      requestLockRef.current = false;
    }
  };

  return (
    <section
      className="app-card p-5 sm:p-6 min-w-0"
      data-testid={testId}
      aria-labelledby={`${testId}-heading`}
    >
      <SectionHeader
        title="What my coach remembers"
        description="Your coach can use saved preferences to personalize future coaching. Saved preferences are context only and do not override your activity, sensor, profile, readiness, health, or training-plan data."
        testId={`${testId}-header`}
      />
      <h2 id={`${testId}-heading`} className="sr-only">Coaching memory settings</h2>

      {loading && (
        <p className="text-sm text-dark-text-muted flex items-center gap-2" role="status">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Loading saved preferences…
        </p>
      )}

      {!loading && loadError && (
        <div role="alert">
          <p className="text-sm text-analytics-amber">{loadError}</p>
          <Button type="button" size="sm" variant="outline" className="mt-3" onClick={refreshList}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !loadError && data.items.length === 0 && (
        <EmptyState
          compact
          icon={Brain}
          title="No saved preferences"
          description="Your coach does not currently have any saved coaching preferences."
          testId={`${testId}-empty`}
          className="mt-2"
        />
      )}

      {!loading && !loadError && grouped.globalItems.length > 0 && (
        <div className="space-y-3" data-testid={`${testId}-global`}>
          <h3 className="card-label">Global preferences</h3>
          {grouped.globalItems.map((item) => (
            <MemoryRow
              key={item.presentationId}
              item={item}
              uiState={itemStates[item.presentationId] || MEMORY_ITEM_UI_STATE.IDLE}
              confirmationSummary={itemSummaries[item.presentationId]}
              errorMessage={itemErrors[item.presentationId]}
              disabled={destructiveBusy && itemStates[item.presentationId] !== MEMORY_ITEM_UI_STATE.AWAITING_CONFIRMATION}
              onForget={() => handleForgetItem(item.presentationId, item.forgetCommand)}
              onConfirm={() => handleConfirmItem(item.presentationId)}
              onCancel={() => handleCancelItem(item.presentationId)}
              testId={`${testId}-item-${item.presentationId}`}
            />
          ))}
        </div>
      )}

      {!loading && !loadError && grouped.sportGroups.map(([sport, items]) => (
        <div key={sport} className="mt-6 space-y-3" data-testid={`${testId}-sport-${sport}`}>
          <SportBadge sport={sport} />
          {items.map((item) => (
            <MemoryRow
              key={item.presentationId}
              item={item}
              uiState={itemStates[item.presentationId] || MEMORY_ITEM_UI_STATE.IDLE}
              confirmationSummary={itemSummaries[item.presentationId]}
              errorMessage={itemErrors[item.presentationId]}
              disabled={destructiveBusy && itemStates[item.presentationId] !== MEMORY_ITEM_UI_STATE.AWAITING_CONFIRMATION}
              onForget={() => handleForgetItem(item.presentationId, item.forgetCommand)}
              onConfirm={() => handleConfirmItem(item.presentationId)}
              onCancel={() => handleCancelItem(item.presentationId)}
              testId={`${testId}-item-${item.presentationId}`}
            />
          ))}
        </div>
      ))}

      {!loading && !loadError && data.forgetAllCommand && (
        <div className="mt-8 pt-5 border-t border-[var(--border-primary)]" data-testid={`${testId}-forget-all`}>
          {forgetAllState === FORGET_ALL_UI_STATE.EXECUTED && (
            <p className="text-sm text-analytics-green mb-3" role="status">
              The confirmed saved preferences were removed. Your current saved preferences have been refreshed.
            </p>
          )}
          {forgetAllState === FORGET_ALL_UI_STATE.UNCERTAIN && (
            <p className="text-sm text-analytics-amber mb-3" role="status">
              {forgetAllError || "We couldn't confirm the outcome. Refresh to check."}
            </p>
          )}
          {forgetAllState === FORGET_ALL_UI_STATE.AWAITING_CONFIRMATION && (
            <div className="mb-4 space-y-3" data-testid={`${testId}-forget-all-confirmation`}>
              <p className="text-sm text-dark-text-secondary whitespace-pre-wrap">{forgetAllSummary}</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" disabled={requestLockRef.current} onClick={handleForgetAllConfirm}>
                  Confirm
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={requestLockRef.current} onClick={handleForgetAllCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            disabled={destructiveBusy || forgetAllState === FORGET_ALL_UI_STATE.AWAITING_CONFIRMATION}
            onClick={() => setShowForgetAllDialog(true)}
            aria-haspopup="dialog"
          >
            Forget all coaching memories
          </Button>
        </div>
      )}

      {showForgetAllDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${testId}-forget-all-dialog-title`}
          data-testid={`${testId}-forget-all-dialog`}
        >
          <div className="app-card max-w-md w-full p-6">
            <h3 id={`${testId}-forget-all-dialog-title`} className="section-title text-base">
              Forget all saved coaching preferences?
            </h3>
            <p className="text-sm text-dark-text-secondary mt-3">
              This will remove the preferences your coach has explicitly saved for personalization
              across all sports.
            </p>
            <p className="text-sm text-dark-text-muted mt-3">
              This will not delete your activities, sensor data, profile, plans, or fitness/readiness data.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForgetAllDialog(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleForgetAllContinue}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
